"use client";

import { useState, useMemo } from "react";
import { Settings2 } from "lucide-react";
import type { FilterChangeHandler } from "./types";
import { type BookWeights, OFFICIAL_PRESETS } from "@/lib/weights-config";
import { WeightsDialog } from "@/components/weights-dialog";
import { getFilterPillClassName } from "@/components/tools/filter-chrome";
import type { UserWeightPreset } from "@/lib/actions/weight-presets";
import { cn } from "@/lib/utils";

function weightsMatch(a: BookWeights | null, b: BookWeights | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k, i) => k === bKeys[i] && a[k] === b[k]);
}

export interface WeightsFilterProps {
  value: BookWeights | null;
  presetName?: string | null;
  requiredBooks?: string[];
  onChange: FilterChangeHandler;
  disabled?: boolean;
  className?: string;
  userPresets?: UserWeightPreset[];
  onSavePreset?: (name: string, weights: BookWeights, requiredBooks: string[]) => Promise<void>;
  onDeletePreset?: (name: string) => Promise<void>;
  onSetDefault?: (name: string | null) => Promise<void>;
  variant?: "default" | "compact";
  allowedBooks?: readonly string[];
  guidedPresetKey?: string;
}

export function WeightsFilter({
  value,
  presetName,
  requiredBooks = [],
  onChange,
  disabled,
  className,
  userPresets = [],
  onSavePreset,
  onDeletePreset,
  onSetDefault,
  variant = "default",
  allowedBooks,
  guidedPresetKey,
}: WeightsFilterProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasCustomWeights = value !== null;
  const hasRequiredBooks = requiredBooks.length > 0;
  const isActive = hasCustomWeights || hasRequiredBooks;

  const inferredPresetName = useMemo(() => {
    if (presetName || !value) return null;
    if (userPresets.length > 0) {
      const userMatch = userPresets.find(p => weightsMatch(p.weights, value));
      if (userMatch) return userMatch.name;
    }
    const officialMatch = OFFICIAL_PRESETS.find(p => weightsMatch(p.weights, value));
    return officialMatch?.label ?? null;
  }, [presetName, value, userPresets]);

  const displayPresetName = presetName ?? inferredPresetName;
  const activeLabel = displayPresetName
    ? displayPresetName
    : hasCustomWeights
      ? `${Object.keys(value).length} book${Object.keys(value).length !== 1 ? "s" : ""}`
      : hasRequiredBooks
        ? `Weights · ${requiredBooks.length} req`
        : "Weights";

  const compactClassName = cn(
    "inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium min-w-[100px]",
    "bg-black/20 border border-white/10",
    "text-foreground transition-all duration-200",
    "hover:bg-black/40 hover:border-white/20",
    "focus:outline-none focus:ring-1 focus:ring-primary/20 focus:border-primary/50",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    isActive && "bg-primary/10 border-primary/30 text-primary",
    className
  );

  return (
    <>
      <button
        type="button"
        data-spotlight-target={guidedPresetKey && displayPresetName !== OFFICIAL_PRESETS.find((preset) => preset.key === guidedPresetKey)?.label ? "Click Weights" : undefined}
        onClick={() => setDialogOpen(true)}
        disabled={disabled}
        className={variant === "compact" ? compactClassName : getFilterPillClassName({ active: isActive, className })}
      >
        {variant !== "compact" && (
          <>
            <Settings2 className="size-3.5" />
            <span className="text-xs text-muted-foreground font-semibold tracking-wide">Fair Value</span>
          </>
        )}
        <span
          className={cn(
            "truncate max-w-[140px]",
            variant === "compact" ? "text-sm" : "font-medium text-foreground"
          )}
          title={activeLabel}
        >
          {activeLabel}
        </span>
      </button>

      <WeightsDialog
        guidedPresetKey={guidedPresetKey}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        value={value}
        activePresetName={displayPresetName}
        requiredBooks={requiredBooks}
        onApply={(weights, newPresetName, newRequiredBooks) => onChange({ weights, preset: newPresetName, requiredBooks: newRequiredBooks }, { immediate: true })}
        userPresets={userPresets}
        onSavePreset={onSavePreset}
        onDeletePreset={onDeletePreset}
        onSetDefault={onSetDefault}
        availableBooks={allowedBooks}
      />
    </>
  );
}
